package com.bikompanion.ui.home

import dev.zacsweers.metro.Assisted
import dev.zacsweers.metro.AssistedFactory
import dev.zacsweers.metro.AssistedInject
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.StationWatchRepository
import com.bikompanion.domain.StationWatchUpdate
import com.bikompanion.domain.usecase.CancelStationWatchUseCase
import com.bikompanion.domain.usecase.EvaluateStationWatchesUseCase
import com.bikompanion.domain.usecase.ExpireStationWatchesUseCase
import com.bikompanion.domain.usecase.StartStationWatchUseCase
import com.bikompanion.ui.watch.StationWatchEvent
import kotlin.time.Clock
import kotlin.time.Duration.Companion.milliseconds

/** The stations a rider asked to be told about, while a screen is there to show them. */
@AssistedInject
class StationWatchTracker(
    @Assisted private val session: HomeSession,
    private val watchRepository: StationWatchRepository,
    private val expireStationWatches: ExpireStationWatchesUseCase,
    private val evaluateStationWatches: EvaluateStationWatchesUseCase,
    private val startStationWatch: StartStationWatchUseCase,
    private val cancelStationWatch: CancelStationWatchUseCase,
) {
    @AssistedFactory
    fun interface Factory {
        fun create(session: HomeSession): StationWatchTracker
    }

    private val state get() = session.state

    /** Off while the screen is stopped, when a timer would have nothing to report to. */
    private var expiryTimerEnabled = false
    private var expiryJob: Job? = null

    fun watch(
        station: BikeStation,
        bikeType: BikeType,
    ) {
        val now = nowEpochMillis()
        process(expireStationWatches(now))
        val watch = startStationWatch(station, bikeType, now)
        publish()
        scheduleExpiry()
        report(StationWatchEvent.Started(watch))
    }

    fun cancel(
        station: BikeStation,
        bikeType: BikeType,
    ) {
        expireNow()
        cancelStationWatch(station.networkId, station.id, bikeType)
        publish()
        scheduleExpiry()
        if (state.value.stationWatches.isEmpty()) report(StationWatchEvent.Stopped)
    }

    fun publish() {
        val active = expireStationWatches(nowEpochMillis()).activeWatches
        state.update { it.copy(stationWatches = active) }
    }

    fun reload(reportExpired: Boolean) {
        val update = expireStationWatches(nowEpochMillis())
        if (reportExpired) {
            process(update)
        } else {
            state.update { it.copy(stationWatches = update.activeWatches) }
        }
        scheduleExpiry()
    }

    fun watchedNetworkIds(): List<String> = watchRepository.watches().map { it.networkId }.distinct()

    fun expireNow() = process(expireStationWatches(nowEpochMillis()))

    /** Checks [refreshedNetworkIds], the networks that just answered live, for a watched bike. */
    fun evaluate(refreshedNetworkIds: Set<String>) {
        process(
            evaluateStationWatches(
                stationsByNetwork = state.value.stationsByNetwork,
                refreshedNetworkIds = refreshedNetworkIds,
                nowEpochMillis = nowEpochMillis(),
            ),
        )
        scheduleExpiry()
    }

    fun startExpiryTimer() {
        expiryTimerEnabled = true
        scheduleExpiry()
    }

    fun stopExpiryTimer() {
        expiryTimerEnabled = false
        expiryJob?.cancel()
        expiryJob = null
    }

    private fun scheduleExpiry() {
        expiryJob?.cancel()
        if (!expiryTimerEnabled) return
        val deadline = watchRepository.watches().minOfOrNull { it.expiresAtEpochMillis } ?: return
        expiryJob = session.scope.launch {
            val remaining = deadline - nowEpochMillis()
            if (remaining > 0) delay(remaining.milliseconds)
            expireNow()
            expiryJob = null
            scheduleExpiry()
        }
    }

    private fun process(update: StationWatchUpdate) {
        state.update { it.copy(stationWatches = update.activeWatches) }
        if (update.expiredWatches.isNotEmpty()) {
            report(StationWatchEvent.Expired(update.expiredWatches, update.activeWatches.isNotEmpty()))
        }
        if (update.foundWatches.isNotEmpty()) {
            report(StationWatchEvent.Found(update.foundWatches))
        }
    }

    /** Queues [event] for the screen, which hands it to the host and then consumes it. */
    fun report(event: StationWatchEvent) {
        state.update { it.copy(pendingStationWatchEvents = it.pendingStationWatchEvents + event) }
    }

    private fun nowEpochMillis() = Clock.System.now().toEpochMilliseconds()
}
